import { t } from 'i18next';
import { useMemo } from 'react';
import { useParams } from 'react-router';
import { useAllClasses, useAllSpecs } from '../../../../../../shared/frontend/scripts/selectors/global';
import { TClassSummary, TSpecSummary } from '../../../../../../shared/types/utilityTypes';

export const createSpecPageSlug = (classSlug: string, specSlug: string) => `${specSlug}-${classSlug}`;

export const resolveSpecPageSlug = (
  pageSlug: string | undefined,
  classes: TClassSummary[],
  specs: TSpecSummary[]
): { cls: TClassSummary | null; spec: TSpecSummary | null } => {
  if (!pageSlug) {
    return { cls: null, spec: null };
  }

  const candidateClasses = [...classes].sort((a, b) => b.slug.length - a.slug.length);

  for (const cls of candidateClasses) {
    const suffix = `-${cls.slug}`;

    if (!pageSlug.endsWith(suffix)) {
      continue;
    }

    const specSlug = pageSlug.slice(0, -suffix.length);

    if (!specSlug) {
      continue;
    }

    const spec = specs.find((item) => item.class_id === cls.id && item.slug === specSlug) ?? null;

    if (spec) {
      return { cls, spec };
    }
  }

  return { cls: null, spec: null };
};

export const useSpecPageNames = () => {
  const params = useParams<{ specSlug?: string }>();
  const classes = useAllClasses();
  const specs = useAllSpecs();

  return useMemo(() => {
    const { cls, spec } = resolveSpecPageSlug(params.specSlug, classes, specs);
    if (!cls || !spec) {
      return null;
    }
    return {
      cls,
      spec,
      specName: t(`game:specs.${cls.slug}.${spec.slug}.name`),
      className: t(`game:classes.${cls.slug}.name`)
    };
  }, [params.specSlug, classes, specs]);
};
